import React, { useEffect, useRef, useState, useCallback } from "react";
import Sidebar from "../../Components/Chatbot/Sidebar";
import ConfirmationCard from "../../Components/Chatbot/ConfirmationCard";
import SoftConfirmToast from "../../Components/Chatbot/SoftConfirmToast";
import FileDropZone, { FileDropZoneRef, AttachedFile, MAX_FILE_SIZE, ALLOWED_EXTENSIONS } from "../../Components/Chatbot/FileDropZone";
import UndoButton from "../../Components/Chatbot/UndoButton";
import StepsTrace from "../../Components/Chatbot/StepsTrace";
import {
  MessageSquare,
  Send,
  Copy,
  Check,
  ArrowDown,
  PanelLeftClose,
  PanelLeft,
  Paperclip,
  X,
  FileText,
} from "lucide-react";
import { getAllMessage, getAiSettings, uploadChatFile } from "../../utils/API";
import { formatAIResponse } from "../../utils/formatAIResponse";
import { useParams, useNavigate } from "react-router-dom";

/* ─────────────────────────────────────────────────────────────────────────────
   CSS injected once at module level — avoids re-injecting on every render
───────────────────────────────────────────────────────────────────────────── */
const LOADER_STYLE = `
  @keyframes dotBounce {
    0%, 80%, 100% { transform: scale(0.8); opacity: 0.3; }
    40%            { transform: scale(1.1); opacity: 1;   }
  }
  @keyframes pulse {
    0%, 80%, 100% { transform: scale(0.6); opacity: 0.35; }
    40%            { transform: scale(1); opacity: 1;   }
  }
  @keyframes fadeSlideIn {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .chat-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: #0C447C;
    animation: dotBounce 1.4s infinite ease-in-out;
    display: inline-block;
  }
  .chat-dot:nth-child(1) { animation-delay: -0.32s; }
  .chat-dot:nth-child(2) { animation-delay: -0.16s; }
  .chat-dot:nth-child(3) { animation-delay: 0s; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .spin { animation: spin 0.8s linear infinite; }
  .chat-resize-handle {
    width: 4px;
    flex-shrink: 0;
    cursor: col-resize;
    background: transparent;
    transition: background 0.15s;
    position: relative;
    z-index: 5;
  }
  .chat-resize-handle:hover {
    background: rgba(12, 68, 124, 0.25);
  }
  .chat-scroll-zone {
    scrollbar-width: thin;
    scrollbar-color: #e5e7eb transparent;
  }
  .chat-scroll-zone::-webkit-scrollbar {
    width: 6px;
  }
  .chat-scroll-zone::-webkit-scrollbar-track {
    background: transparent;
  }
  .chat-scroll-zone::-webkit-scrollbar-thumb {
    background: #e5e7eb;
    border-radius: 10px;
    transition: background 0.2s;
  }
  .chat-scroll-zone::-webkit-scrollbar-thumb:hover {
    background: #d1d5db;
  }
  /* ── Model selector dropdown ── */
  .model-selector-wrap {
    position: relative;
    display: inline-flex;
    align-items: center;
  }
  .model-selector-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 999px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    font-size: 12px;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    transition: all 0.18s;
    white-space: nowrap;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .model-selector-btn:hover {
    border-color: #0C447C;
    color: #0C447C;
    background: #eff6ff;
  }
  .model-selector-btn svg.chevron {
    flex-shrink: 0;
    transition: transform 0.2s;
  }
  .model-selector-btn.open svg.chevron {
    transform: rotate(180deg);
  }
  .model-dropdown {
    position: absolute;
    bottom: calc(100% + 8px);
    right: 0;
    min-width: 260px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.13);
    padding: 6px;
    z-index: 50;
    animation: dropIn 0.15s ease;
  }
  @keyframes dropIn {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .model-option {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.15s;
    font-size: 13px;
    color: #374151;
  }
  .model-option:hover {
    background: #f0f6ff;
    color: #0C447C;
  }
  .model-option.selected {
    background: #eff6ff;
    color: #0C447C;
    font-weight: 600;
  }
  .model-option .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #d1d5db;
    flex-shrink: 0;
  }
  .model-option.selected .dot {
    background: #0C447C;
  }
`;

const isImageAttachment = (filename?: string) =>
  /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(String(filename || "").split("?")[0]);

if (
  typeof document !== "undefined" &&
  !document.getElementById("chat-dot-style")
) {
  const s = document.createElement("style");
  s.id = "chat-dot-style";
  s.textContent = LOADER_STYLE;
  document.head.appendChild(s);
}

const QuickQueries = [
  "Show all active projects",
  "Task summary",
  "provide me leads summary",
  "pending leave requests",
];

interface ActionLogEntry {
  actionLogId: string;
  collectionName: string;
  operation: string;
  undoWindowSeconds: number;
}

interface Message {
  _id: string;
  sender: "user" | "ai";
  content: string;
  createdAt?: string;
  isLoading?: boolean;
  isConfirmationLoading?: boolean;
  status?: string;
  steps?: string[];
  isInteractive?: boolean;
  interactiveData?: any;
  formData?: any;
  formError?: string;
  isHardConfirm?: boolean;
  hardConfirmData?: {
    pendingActionId: string;
    operations: { collectionName: string; op: string; label?: string; dataPreview?: string }[];
    conversationId?: string;
  };
  actionLogs?: ActionLogEntry[];
  attachments?: string[];
}

const getLoggedInUserName = (): string => {
  try {
    const storedAuthData = localStorage.getItem("authData");
    if (!storedAuthData) return "";
    const parsed = JSON.parse(storedAuthData);
    return (
      parsed?.user?.name ||
      parsed?.user?.fullName ||
      parsed?.user?.username ||
      parsed?.user?.firstName ||
      ""
    );
  } catch {
    return "";
  }
};

const getUserInitials = (name: string): string => {
  const words = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) return "UA";

  const initials = words.map((word) => word.charAt(0).toUpperCase()).join("");
  if (initials.length > 0) return initials.slice(0, 3);

  return name.trim().slice(0, 2).toUpperCase() || "UA";
};

/* ── Three-dot animated loader ──────────────────────────────────────────── */
const ThreeDotLoader: React.FC = () => (
  <div className="flex items-center gap-1 py-1 px-1">
    <span className="chat-dot" />
    <span className="chat-dot" />
    <span className="chat-dot" />
  </div>
);

const getVisibleAIText = (text: string = ""): string =>
  String(text)
    .replace(/<(thinking|think)>[\s\S]*?(?:<\/(thinking|think)>|$)/gi, "")
    .replace(/<function_calls?>[\s\S]*?(?:<\/function_calls?>|$)/gi, "")
    .replace(/<tool_calls?>[\s\S]*?(?:<\/tool_calls?>|$)/gi, "");

const shouldSendAttachmentsForPrompt = (text: string = "", hasFiles = false): boolean => {
  const input = String(text || "");
  if (!hasFiles) return false;
  if (!input.trim()) return true;

  return /\b(proposals?|purposals?|documents?|files?|images?|photos?|attachments?|attached|upload(?:ed)?|prd|contract|resume|cv|pdf|docx?|xlsx?|csv|txt|png|jpe?g|webp|screenshot)\b/i.test(input) ||
    /\b(this|these|that)\b/i.test(input);
};

/* ── Model Selector (Claude-style chip + dropdown) ──────────────────────── */
interface ModelOption { label: string; value: string; isActive?: boolean }
interface ModelSelectorProps {
  options: ModelOption[];
  selected: string | null;
  onChange: (id: string) => void;
  disabled?: boolean;
}
const ModelSelector: React.FC<ModelSelectorProps> = ({ options, selected, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedOption = options.find((o) => o.value === selected);
  const label = selectedOption ? selectedOption.label : (options.length === 0 ? "No models" : "Select model");

  if (disabled) {
    return (
      <span className="model-selector-btn" style={{ cursor: "not-allowed", opacity: 0.5 }}>
        <span style={{ fontSize: 14 }}>🤖</span>
        <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
      </span>
    );
  }

  return (
    <div className="model-selector-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`model-selector-btn${open ? " open" : ""}`}
        onClick={() => setOpen((p) => !p)}
        title="Select AI model"
        style={{ cursor: "pointer" }}
      >
        <span style={{ fontSize: 14 }}>🤖</span>
        <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
        <svg className="chevron" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2 4.5L6 8.5L10 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && options.length > 0 && (
        <div className="model-dropdown">
          <p style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", padding: "4px 12px 8px" }}>
            Available Models
          </p>
          {options.map((opt) => (
            <div
              key={opt.value}
              className={`model-option${opt.value === selected ? " selected" : ""}`}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              <span className="dot" />
              <span style={{ flex: 1 }}>{opt.label}</span>
              {opt.value === selected && (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2.5 7L5.5 10L11.5 4" stroke="#0C447C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/* ═══════════════════════════════════════════════════════════════════════════
   CHATBOT COMPONENT
   Layout — two-column flex row:
     • Left  : always-visible, resizable Sidebar
     • Right : flex-col chat column
       ├─ [header]   flex-shrink-0 — sticky, never scrolls
       ├─ [msgs]     flex-1 + min-h-0 — ONLY scrollable zone
       └─ [input]    flex-shrink-0 — always visible, never hides

   Chat selection:
     • selectedChatId is PRIMARY state — URL is secondary (bookmark sync only)
     • handleChatSelect → updates state + URL (replace, no remount)
     • useParams urlId syncs on direct URL navigation (browser back/forward)
═══════════════════════════════════════════════════════════════════════════ */
const Chatbot: React.FC = () => {
  const { id: urlId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  /* ── Primary state ──────────────────────────────────────────────────── */
  const [selectedChatId, setSelectedChatId] = useState<string | null>(() => {
    // Restore last chat from URL param, then localStorage fallback
    return urlId || localStorage.getItem("lastChatId") || null;
  });
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(260);
  const [historyOpen, setHistoryOpen] = useState(() => {
    const saved = localStorage.getItem("chatHistoryOpen");
    return saved !== "false";
  });

  // Model settings for the Claude-like dropdown selector
  const [modelOptions, setModelOptions] = useState<{ label: string; value: string }[]>([]);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);

  // Thinking state — shown above the input area
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingStatus, setThinkingStatus] = useState<string>("");
  // Live ordered list of pipeline steps for the in-progress message (Claude-style trace).
  const [liveSteps, setLiveSteps] = useState<string[]>([]);
  const liveStepsRef = useRef<string[]>([]);

  // Agentic feature states
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [softConfirms, setSoftConfirms] = useState<{
    id: string;
    actionLogIds: string[];
    operations: { collectionName: string; op: string }[];
    undoWindowSeconds: number;
  }[]>([]);
  const [undoneActionIds, setUndoneActionIds] = useState<Set<string>>(new Set());
  const fileDropZoneRef = useRef<FileDropZoneRef>(null);

  /* ── Refs ───────────────────────────────────────────────────────────── */
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollZoneRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const promptHistoryIndexRef = useRef<number | null>(null);
  const promptDraftRef = useRef("");
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartWidthRef = useRef(0);
  const isInitialLoadRef = useRef(false);
  const streamingIdRef = useRef<string | null>(null);

  const brandBlue = "bg-[#0C447C]";
  const loggedInUserName = getLoggedInUserName();
  const userInitials = getUserInitials(loggedInUserName);

  // Removed global scroll lock to allow normal page scrolling

  useEffect(() => {
    promptHistoryIndexRef.current = null;
    promptDraftRef.current = "";
  }, [selectedChatId]);

  /* ── Persist last chat ID to localStorage ──────────────────────────── */
  useEffect(() => {
    if (selectedChatId) {
      localStorage.setItem("lastChatId", selectedChatId);
    }
  }, [selectedChatId]);

  /* ── On mount: if no urlId but we have a saved chat, redirect to it ── */
  useEffect(() => {
    const savedId = localStorage.getItem("lastChatId");
    if (!urlId && savedId) {
      navigate(`/chat/${savedId}`, { replace: true });
    }
  }, []); // runs once on mount

  /* ── Sync selectedChatId when URL changes (browser back/forward / direct link) */
  useEffect(() => {
    if (urlId && urlId !== selectedChatId) {
      setSelectedChatId(urlId);
    } else if (!urlId && selectedChatId !== null) {
      // Navigated to /chat (new chat) — only reset if explicitly triggered
    }
  }, [urlId]); // intentionally omits selectedChatId — only reacts to URL param changes

  /* ── Load AI model configurations on mount ──────────────────────────── */
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const res = await getAiSettings({ limit: 50, onlyActive: true });
        if (res.success && res.data?.length > 0) {
          const opts = res.data.map((s: any) => ({
            label: `${s.title} (${s.model})`,
            value: s._id,
            isActive: s.isActive,
          }));
          setModelOptions(opts);
          // Pre-select the first active model
          setSelectedModelId(res.data[0]._id);
        } else {
          setModelOptions([]);
          setSelectedModelId(null);
        }
      } catch (err) {
        console.error("Failed to load AI models:", err);
      }
    };
    fetchModels();
  }, []);

  /* ── Load messages whenever selectedChatId changes ─────────────────── */
  useEffect(() => {
    const loadChat = async () => {
      if (selectedChatId) {
        setLoading(true);
        isInitialLoadRef.current = true;
        try {
          const res = await getAllMessage(selectedChatId);
          const msgs: Message[] = Array.isArray(res)
            ? res
            : res.data || res.messages || [];
          setMessages(msgs);
          // Sync the model that was used in this conversation
          if (res.aiSettingId) {
            setSelectedModelId(res.aiSettingId);
          }
          // Scroll after messages are set and DOM updates
          setTimeout(() => {
            const el = scrollZoneRef.current;
            if (el) el.scrollTop = el.scrollHeight;
            setShowScrollToBottom(false);
            isInitialLoadRef.current = false;
          }, 50);
        } catch (err) {
          console.error(err);
          setMessages([]);
          isInitialLoadRef.current = false;
        } finally {
          setLoading(false);
        }
      } else {
        setMessages([]);
      }
    };
    loadChat();
  }, [selectedChatId]);

  /* ── Chat selection from sidebar — updates state, syncs URL (no remount) */
  const handleChatSelect = useCallback(
    (chatId: string) => {
      if (chatId === selectedChatId) return; // Already selected — skip redundant load
      setMessages([]);
      setShowScrollToBottom(false);
      setSelectedChatId(chatId);
      navigate(`/chat/${chatId}`, { replace: true }); // Update URL without adding history entry
    },
    [selectedChatId, navigate],
  );

  /* ── New chat handler ───────────────────────────────────────────────── */
  const handleNewChat = useCallback(() => {
    setSelectedChatId(null);
    setMessages([]);
    setShowScrollToBottom(false);
    localStorage.removeItem("lastChatId");
    navigate("/chat", { replace: true });
  }, [navigate]);

  /* ── Auto-scroll to bottom ──────────────────────────────────────────── */
  useEffect(() => {
    if (isInitialLoadRef.current) {
      // Wait for DOM to paint then instantly jump to bottom
      const timer = setTimeout(() => {
        const el = scrollZoneRef.current;
        if (el) el.scrollTop = el.scrollHeight;
        isInitialLoadRef.current = false;
      }, 0);
      return () => clearTimeout(timer);
    } else {
      // Smooth scroll for new messages being sent
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  /* ── Auto-grow textarea ─────────────────────────────────────────────── */
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 180) + "px";
  }, [inputValue]);

  /* ── Scroll handler: show "jump to latest" once user is away from bottom */
  const handleScroll = useCallback(() => {
    const el = scrollZoneRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollToBottom(distanceFromBottom > 180);
  }, []);

  /* ── Wheel handler: Removed to allow native smooth scrolling ─────────── */

  /* ── Sidebar resize drag ────────────────────────────────────────────── */
  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDraggingRef.current = true;
      dragStartXRef.current = e.clientX;
      dragStartWidthRef.current = sidebarWidth;

      const onMouseMove = (ev: MouseEvent) => {
        if (!isDraggingRef.current) return;
        const diff = ev.clientX - dragStartXRef.current;
        const newWidth = Math.min(
          Math.max(dragStartWidthRef.current + diff, 180),
          420,
        );
        setSidebarWidth(newWidth);
      };

      const onMouseUp = () => {
        isDraggingRef.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [sidebarWidth],
  );

  const toggleHistory = useCallback(() => {
    setHistoryOpen((prev) => {
      const next = !prev;
      localStorage.setItem("chatHistoryOpen", String(next));
      return next;
    });
  }, []);

  const scrollToBottom = () => {
    const el = scrollZoneRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    setShowScrollToBottom(false);
  };

  /* ── Copy to clipboard ──────────────────────────────────────────────── */
  const copyToClipboard = async (text: string, messageId: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopiedMessageId(messageId);
    setTimeout(() => setCopiedMessageId(null), 2000);
  };

  /* ── File drag-drop handlers ────────────────────────────────────────── */
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only leave if we truly left the drop zone (not entering a child)
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    if (
      e.clientX <= rect.left ||
      e.clientX >= rect.right ||
      e.clientY <= rect.top ||
      e.clientY >= rect.bottom
    ) {
      setIsDragOver(false);
    }
  }, []);

  const handleFileDrop = useCallback(async (files: FileList) => {
    setIsDragOver(false);
    const file = files[0];
    if (!file) return;

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setMessages((prev) => [
        ...prev,
        {
          _id: Date.now().toString(),
          sender: "ai",
          content: `File too large. Maximum size is 10MB.`,
          createdAt: new Date().toISOString(),
        },
      ]);
      return;
    }

    // Validate file extension
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setMessages((prev) => [
        ...prev,
        {
          _id: Date.now().toString(),
          sender: "ai",
          content: `Unsupported file type ".${ext}". Allowed: PDF, CSV, XLSX, DOCX, TXT, PNG, JPG, JPEG.`,
          createdAt: new Date().toISOString(),
        },
      ]);
      return;
    }

    setFileUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await uploadChatFile(formData);
      if (res?.success && res?.data) {
        setAttachedFiles((prev) => [
          ...prev,
          {
            fileId: res.data.fileId,
            originalName: res.data.originalName,
            filePath: res.data.filePath,
            charCount: res.data.charCount,
            mimeType: res.data.mimeType,
          },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            _id: Date.now().toString(),
            sender: "ai",
            content: res?.error || "File upload failed. Please try again.",
            createdAt: new Date().toISOString(),
          },
        ]);
      }
    } catch (err: any) {
      const errMsg = err?.response?.data?.error || err?.message || "File upload failed.";
      setMessages((prev) => [
        ...prev,
        {
          _id: Date.now().toString(),
          sender: "ai",
          content: errMsg,
          createdAt: new Date().toISOString(),
        },
      ]);
    } finally {
      setFileUploading(false);
    }
  }, []);

  const handleRemoveFile = useCallback((fileId: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f.fileId !== fileId));
  }, []);

  /* ── Hard confirmation handlers ─────────────────────────────────────── */
  const handleHardConfirm = useCallback(async (pendingActionId: string) => {
    // Find the confirmation message to get context
    const confirmMsg = messages.find((m) => m.hardConfirmData?.pendingActionId === pendingActionId);
    const confirmMsgIndex = messages.findIndex((m) => m.hardConfirmData?.pendingActionId === pendingActionId);
    const userMsgForConfirm = confirmMsgIndex > 0
      ? [...messages.slice(0, confirmMsgIndex)].reverse().find((m) => m.sender === "user")
      : undefined;
    const hardData = confirmMsg?.hardConfirmData;
    if (!hardData) return;

    // Remove the confirmation card message and start streaming the confirmed action
    const streamingId = "ai-streaming-" + Date.now();
    setMessages((prev) => [
      ...prev.filter((m) => m._id !== confirmMsg!._id),
      {
        _id: streamingId,
        sender: "ai",
        content: "",
        isLoading: true,
        isConfirmationLoading: true,
        createdAt: new Date().toISOString(),
      },
    ]);
    streamingIdRef.current = streamingId;

    // The backend's confirmAction returns an SSE stream — handle it like sendMessage
    try {
      setIsSending(true);
      setIsThinking(true);
      setThinkingStatus("");
      setLiveSteps([]);
      liveStepsRef.current = [];
      const baseUrl = process.env.REACT_APP_BASE_URL || "";
      const token = localStorage.getItem("token") || "";

      const response = await fetch(`${baseUrl}/chat/confirm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          pendingActionId,
          confirmed: true,
          conversationId: hardData.conversationId || selectedChatId,
          aiSettingId: selectedModelId,
        }),
      });

      if (!response.ok || !response.body) throw new Error(`Server error: ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let accumulatedText = "";
      let finalMeta: any = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith("data: ")) continue;
          try {
            const json = JSON.parse(trimmed.slice(6));
            if (json.error) throw new Error(json.error);

            if (json.softConfirmNotification) {
              const sc = json.softConfirmNotification;
              setSoftConfirms((prev) => [
                ...prev,
                {
                  id: Date.now().toString() + "-soft",
                  actionLogIds: sc.actionLogIds || [],
                  operations: sc.operations || [],
                  undoWindowSeconds: sc.undoWindowSeconds || 5,
                },
              ]);
            }

            if (json.done) {
              finalMeta = json;
              continue;
            }

            if (json.status) {
              const nextStatus = String(json.status || "").trim();
              const nextKey = nextStatus.toLowerCase().replace(/\s+/g, " ");
              const alreadyShown = liveStepsRef.current.some(
                (step) => step.toLowerCase().replace(/\s+/g, " ") === nextKey,
              );

              setThinkingStatus(nextStatus);
              if (nextStatus && !alreadyShown) {
                liveStepsRef.current = [...liveStepsRef.current, nextStatus];
                setLiveSteps(liveStepsRef.current);
              }
              setMessages((prev) =>
                prev.map((m) =>
                  m._id === streamingId
                    ? {
                        ...m,
                        isLoading: true,
                        status: nextStatus,
                        steps: liveStepsRef.current.length ? [...liveStepsRef.current] : m.steps,
                      }
                    : m,
                ),
              );
              continue;
            }

            if (json.text) {
              accumulatedText += json.text;
              const snapshot = getVisibleAIText(accumulatedText);
              if (!snapshot.trim()) continue;

              setMessages((prev) =>
                prev.map((m) =>
                  m._id === streamingId
                    ? {
                        ...m,
                        isLoading: false,
                        content: snapshot,
                        steps: liveStepsRef.current.length ? [...liveStepsRef.current] : m.steps,
                      }
                    : m
                )
              );
              messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
            }
          } catch { /* ignore malformed SSE */ }
        }
      }

      const finalAiId = finalMeta?.aiMsgId || streamingId;
      const finalConvoId = finalMeta?.conversationId || selectedChatId;
      const actionLogs: ActionLogEntry[] = finalMeta?.actionLogs || [];
      const finalUserAttachments = Array.isArray(finalMeta?.userAttachments)
        ? finalMeta.userAttachments
        : undefined;
      const finalContent = getVisibleAIText(accumulatedText).trim() || "No response generated.";
      const finalSteps = liveStepsRef.current.length ? [...liveStepsRef.current] : undefined;

      setMessages((prev) =>
        prev.map((m) => {
          if (m._id === streamingId) {
            return {
                _id: finalAiId,
                sender: "ai",
                content: finalContent,
                isLoading: false,
                createdAt: finalMeta?.createdAt || new Date().toISOString(),
                steps: finalSteps,
                actionLogs: actionLogs.length > 0 ? actionLogs : undefined,
              };
          }
          if (finalUserAttachments?.length && userMsgForConfirm && m._id === userMsgForConfirm._id) {
            return { ...m, attachments: finalUserAttachments };
          }
          return m;
        })
      );

      if (!selectedChatId && finalConvoId) setSelectedChatId(finalConvoId);
    } catch (err) {
      console.error("[confirmAction stream error]", err);
      setMessages((prev) => [
        ...prev.filter((m) => m._id !== streamingId),
        {
          _id: Date.now().toString(),
          sender: "ai",
          content: "Something went wrong confirming the action. Please try again.",
          createdAt: new Date().toISOString(),
        },
      ]);
    } finally {
      setIsSending(false);
      setIsThinking(false);
      streamingIdRef.current = null;
    }
  }, [messages, selectedChatId, selectedModelId]);

  const handleHardCancel = useCallback((pendingActionId: string) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.hardConfirmData?.pendingActionId === pendingActionId
          ? {
              ...m,
              isHardConfirm: false,
              hardConfirmData: undefined,
              content: "Action cancelled.",
              isLoading: false,
              isInteractive: false,
            }
          : m
      )
    );
    setIsSending(false);
  }, []);

  /* ── Soft confirm dismiss ───────────────────────────────────────────── */
  const handleSoftDismiss = useCallback((id: string) => {
    setSoftConfirms((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const handleSoftUndo = useCallback((actionLogId: string) => {
    setUndoneActionIds((prev) => new Set(prev).add(actionLogId));
  }, []);

  /* ── Undo from UndoButton inside AI bubbles ─────────────────────────── */
  const handleUndoFromBubble = useCallback((actionLogId: string) => {
    setUndoneActionIds((prev) => new Set(prev).add(actionLogId));
  }, []);

  /* ── Core send logic (SSE streaming) ───────────────────────────────── */
  const sendMessage = async (content: string, files: AttachedFile[] = []) => {
    const streamingId = "ai-streaming-" + Date.now();
    const effectiveFiles = shouldSendAttachmentsForPrompt(content, files.length > 0) ? files : [];
    const sentAttachments = effectiveFiles
      .map((f) => f.filePath || f.originalName)
      .filter((name): name is string => Boolean(name));
    const userMsg: Message = {
      _id: `${Date.now()}-user`,
      sender: "user",
      content,
      attachments: sentAttachments,
      createdAt: new Date().toISOString(),
    };
    const aiLoadingMsg: Message = {
      _id: streamingId,
      sender: "ai",
      content: "",
      isLoading: true,
      createdAt: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMsg, aiLoadingMsg]);
    setAttachedFiles([]);
    scrollToBottom();

    // Show thinking indicator OUTSIDE the chat box (above input area)
    setIsThinking(true);
    setThinkingStatus("");
    setLiveSteps([]);
    liveStepsRef.current = [];
    setIsSending(true);
    streamingIdRef.current = streamingId;

    try {
      const baseUrl = process.env.REACT_APP_BASE_URL || "";
      const token = localStorage.getItem("token") || "";
      const payload: Record<string, any> = { content };
      if (selectedChatId) payload.conversationId = selectedChatId;
      if (selectedModelId) payload.aiSettingId = selectedModelId;
      if (effectiveFiles.length > 0) {
        payload.attachments = effectiveFiles.map((f) => ({ fileId: f.fileId }));
      }

      const response = await fetch(`${baseUrl}/chat/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok || !response.body) {
        throw new Error(`Server error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let accumulatedText = "";
      let finalMeta: any = null;
      let aiMsgAddedToChat = true; // the loading bubble is already in the chat

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith("data: ")) continue;

          try {
            const json = JSON.parse(trimmed.slice(6));

            if (json.error) {
              throw new Error(json.error);
            }

            if (json.hardConfirmRequired) {
              setIsThinking(false);
              if (Array.isArray(json.userAttachments) && json.userAttachments.length) {
                setMessages((prev) =>
                  prev.map((m) =>
                    m._id === userMsg._id ? { ...m, attachments: json.userAttachments } : m
                  )
                );
              }
              const confirmMsg: Message = {
                _id: streamingId,
                sender: "ai",
                content: "",
                isLoading: false,
                isHardConfirm: true,
                hardConfirmData: {
                  pendingActionId: json.pendingActionId,
                  operations: json.operations || [],
                  conversationId: json.conversationId || selectedChatId,
                },
                createdAt: new Date().toISOString(),
              };
              if (!aiMsgAddedToChat) {
                setMessages((prev) => [...prev, confirmMsg]);
                aiMsgAddedToChat = true;
              } else {
                setMessages((prev) =>
                  prev.map((m) => m._id === streamingId ? confirmMsg : m)
                );
              }
              return; // Stop stream reading — awaiting user confirmation
            }

            if (json.softConfirmNotification) {
              const sc = json.softConfirmNotification;
              setSoftConfirms((prev) => [
                ...prev,
                {
                  id: Date.now().toString() + "-soft",
                  actionLogIds: sc.actionLogIds || [],
                  operations: sc.operations || [],
                  undoWindowSeconds: sc.undoWindowSeconds || 5,
                },
              ]);
              continue; // Don't stop streaming — soft confirms appear alongside the response
            }

            if (json.interrupt) {
              setIsThinking(false);
              if (!aiMsgAddedToChat) {
                const interactiveMsg: Message = {
                  _id: streamingId,
                  sender: "ai",
                  content: "",
                  isInteractive: true,
                  interactiveData: json,
                  formData: { ...(json.partialData || {}) },
                  createdAt: new Date().toISOString(),
                };
                setMessages((prev) => [...prev, interactiveMsg]);
                aiMsgAddedToChat = true;
              } else {
                setMessages((prev) =>
                  prev.map((m) =>
                    m._id === streamingId
                      ? {
                          ...m,
                          isLoading: false,
                          isInteractive: true,
                          interactiveData: json,
                          formData: { ...(json.partialData || {}) },
                          createdAt: new Date().toISOString(),
                        }
                      : m,
                  ),
                );
              }
              return;
            }

            if (json.done) {
              finalMeta = json;
              continue;
            }

            if (json.status) {
              // Update thinking status + accumulate the step into the live trace
              // (skip duplicate labels even when whitespace/casing differs).
              const nextStatus = String(json.status || "").trim();
              const nextKey = nextStatus.toLowerCase().replace(/\s+/g, " ");
              const alreadyShown = liveStepsRef.current.some(
                (step) => step.toLowerCase().replace(/\s+/g, " ") === nextKey,
              );

              setThinkingStatus(nextStatus);
              if (nextStatus && !alreadyShown) {
                liveStepsRef.current = [...liveStepsRef.current, nextStatus];
                setLiveSteps(liveStepsRef.current);
              }
              setMessages((prev) =>
                prev.map((m) =>
                  m._id === streamingId
                    ? {
                        ...m,
                        isLoading: true,
                        status: nextStatus,
                        steps: liveStepsRef.current.length ? [...liveStepsRef.current] : m.steps,
                      }
                    : m,
                ),
              );
              continue;
            }

            if (json.text) {
              accumulatedText += json.text;
              const snapshot = getVisibleAIText(accumulatedText);
              if (!snapshot.trim()) continue;

              setIsThinking(false);

              if (!aiMsgAddedToChat) {
                // First token: remove thinking indicator, add AI bubble to chat
                setIsThinking(false);
                const aiMsg: Message = {
                  _id: streamingId,
                  sender: "ai",
                  content: snapshot,
                  steps: liveStepsRef.current.length ? [...liveStepsRef.current] : undefined,
                  createdAt: new Date().toISOString(),
                };
                setMessages((prev) => [...prev, aiMsg]);
                aiMsgAddedToChat = true;
              } else {
                // Subsequent tokens: update existing AI bubble
                setMessages((prev) =>
                  prev.map((m) =>
                    m._id === streamingId
                      ? {
                          ...m,
                          isLoading: false,
                          content: snapshot,
                          steps: liveStepsRef.current.length ? [...liveStepsRef.current] : m.steps,
                          createdAt: m.createdAt || new Date().toISOString(),
                        }
                      : m,
                  ),
                );
              }
              messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
            }
          } catch (parseErr) {
            // Ignore malformed SSE lines
          }
        }
      }

      // Finalize: replace temp id with real ids from server
      const finalAiId = finalMeta?.aiMsgId || streamingId;
      const finalConvoId = finalMeta?.conversationId || selectedChatId;
      const finalCreatedAt = finalMeta?.createdAt || new Date().toISOString();
      const actionLogs: ActionLogEntry[] = finalMeta?.actionLogs || [];
      const finalUserAttachments = Array.isArray(finalMeta?.userAttachments)
        ? finalMeta.userAttachments
        : undefined;
      const finalContent = getVisibleAIText(accumulatedText).trim() || "No response generated.";

      const finalSteps = liveStepsRef.current.length ? [...liveStepsRef.current] : undefined;
      setIsThinking(false);
      if (aiMsgAddedToChat) {
        setMessages((prev) =>
          prev.map((m) => {
            if (m._id === streamingId) {
              return {
                  _id: finalAiId,
                  sender: "ai",
                  content: finalContent,
                  steps: finalSteps,
                  createdAt: finalCreatedAt,
                  actionLogs: actionLogs.length > 0 ? actionLogs : undefined,
                };
            }
            if (finalUserAttachments?.length && m._id === userMsg._id) {
              return { ...m, attachments: finalUserAttachments };
            }
            return m;
          }),
        );
      } else {
        setIsThinking(false);
        setMessages((prev) => [
          ...prev.map((m) =>
            finalUserAttachments?.length && m._id === userMsg._id
              ? { ...m, attachments: finalUserAttachments }
              : m
          ),
          {
            _id: finalAiId,
            sender: "ai",
            content: finalContent,
            steps: finalSteps,
            createdAt: finalCreatedAt,
            actionLogs: actionLogs.length > 0 ? actionLogs : undefined,
          },
        ]);
      }

      if (!selectedChatId && finalConvoId) {
        setSelectedChatId(finalConvoId);
      }
      scrollToBottom();
    } catch (err) {
      console.error("[sendMessage stream error]", err);
      setIsThinking(false);
      setMessages((prev) => {
        // Remove any partial streaming message if it exists
        const cleaned = prev.filter((m) => m._id !== streamingId);
        return [
          ...cleaned,
          {
            _id: Date.now().toString(),
            sender: "ai",
            content: "Something went wrong. Please try again.",
            createdAt: new Date().toISOString(),
          },
        ];
      });
    } finally {
      setIsSending(false);
      streamingIdRef.current = null;
    }
  };

  const handleSend = async () => {
    if ((!inputValue.trim() && attachedFiles.length === 0) || isSending || fileUploading) return;
    const text = inputValue;
    const files = attachedFiles;
    promptHistoryIndexRef.current = null;
    promptDraftRef.current = "";
    setInputValue("");
    await sendMessage(text, files);
    setAttachedFiles([]);
  };

  const handleQuickQuery = (query: string) => sendMessage(query);

  const handleInterruptSubmit = async (formData: any, msgId: string, interactiveData: any) => {
    // Merge partialData (pre-filled from planner) with formData (user edits).
    // This ensures CV-extracted fields that the user didn't modify are still included.
    const merged: Record<string, any> = {
      ...(interactiveData?.partialData || {}),
      ...(formData || {}),
    };

    const hasValues = Object.values(merged).some(v => v !== null && v !== undefined && v !== "");
    if (!hasValues) return;

    // Enforce mandatory fields before submitting. The backend also blocks blank
    // required fields, but validating here lets us tell the user exactly which
    // ones are missing instead of silently re-opening the form.
    const requiredFields: any[] = (interactiveData?.allFields || []).filter((f: any) => f.required);
    const stillMissing = requiredFields.filter((f: any) => {
      const v = merged[f.name];
      return v === null || v === undefined || String(v).trim() === "";
    });
    if (stillMissing.length > 0) {
      const labels = stillMissing.map((f: any) => f.label || f.name).join(", ");
      setMessages((prev) => prev.map(m =>
        m._id === msgId ? { ...m, formError: `Please fill the required field(s): ${labels}` } : m
      ));
      return;
    }

    const collectionName: string = interactiveData?.collectionName || "";
    const op: string = interactiveData?.op || "create";

    // Remove the interactive message card
    setMessages((prev) => prev.filter(m => m._id !== msgId));

    // Build the "Proceeding with" message — the planner uses collection+op context to
    // generate the correct create/update op, including dependency lookups for ObjectId refs.
    const info = Object.entries(merged)
      .filter(([, v]) => v !== null && v !== undefined && v !== "")
      .map(([k, v]) => `${k}="${v}"`)
      .join(", ");

    await sendMessage(`Proceeding with ${op} ${collectionName}: ${info}`);
  };

  const getUserPromptHistory = () =>
    messages
      .filter((m) => m.sender === "user" && m.content?.trim())
      .map((m) => m.content);

  const fillPromptFromHistory = (value: string) => {
    setInputValue(value);
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(value.length, value.length);
    });
  };

  const handleInputChange = (value: string) => {
    promptHistoryIndexRef.current = null;
    promptDraftRef.current = "";
    setInputValue(value);
  };

  /* ── Keyboard: Enter sends, Shift+Enter inserts newline, arrows browse prompts ─ */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
      return;
    }

    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;

    const target = e.currentTarget;
    const cursor = target.selectionStart ?? 0;
    const beforeCursor = target.value.slice(0, cursor);
    const afterCursor = target.value.slice(target.selectionEnd ?? cursor);
    const cursorOnFirstLine = !beforeCursor.includes("\n");
    const cursorOnLastLine = !afterCursor.includes("\n");
    const history = getUserPromptHistory();

    if (e.key === "ArrowUp") {
      if (!history.length || !cursorOnFirstLine) return;
      e.preventDefault();

      let nextHistoryIndex: number;
      if (promptHistoryIndexRef.current === null) {
        promptDraftRef.current = inputValue;
        nextHistoryIndex = history.length - 1;
      } else {
        nextHistoryIndex = Math.max(0, promptHistoryIndexRef.current - 1);
      }

      promptHistoryIndexRef.current = nextHistoryIndex;
      fillPromptFromHistory(history[nextHistoryIndex] || "");
      return;
    }

    const currentHistoryIndex = promptHistoryIndexRef.current;
    if (currentHistoryIndex === null || !cursorOnLastLine) return;
    e.preventDefault();

    const nextIndex = currentHistoryIndex + 1;
    if (nextIndex >= history.length) {
      promptHistoryIndexRef.current = null;
      const draft = promptDraftRef.current;
      promptDraftRef.current = "";
      fillPromptFromHistory(draft);
      return;
    }

    promptHistoryIndexRef.current = nextIndex;
    fillPromptFromHistory(history[nextIndex] || "");
  };

  const showThinkingIndicator = isThinking;
  

  /* ═════════════════════════════════════════════════════════════════════
     RENDER
  ═════════════════════════════════════════════════════════════════════ */
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        width: "100%",
        height: "calc(100vh - 64px)", // If header is 64px, adjust as needed
        maxHeight: "800px", // Optional: limit max height for container
        minHeight: 400,
        background: "#fff",
        fontFamily: "inherit",
        boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      {/* ── LEFT PANEL: always-visible, resizable Chat History sidebar ───── */}
      <div
        style={{
          width: historyOpen ? `${sidebarWidth}px` : 0,
          minWidth: historyOpen ? `${sidebarWidth}px` : 0,
          flexShrink: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: "#fff",
          borderRight: historyOpen ? "1px solid #e5e7eb" : "none",
          transition:
            "width 0.4s cubic-bezier(0.4,0,0.2,1), min-width 0.4s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        <Sidebar
          selectedChatId={selectedChatId}
          onChatSelect={handleChatSelect}
          onNewChat={handleNewChat}
        />
      </div>

      {/* ── DRAG HANDLE: user drags this to resize the sidebar ───────────── */}
      {historyOpen && (
        <div
          className="chat-resize-handle"
          onMouseDown={handleDragStart}
          title="Drag to resize sidebar"
          style={{ height: "100%" }}
        />
      )}

      {/* ── RIGHT PANEL: chat column ─────────────────────────────────────── */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minWidth: 0,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* ── CHATBOT HEADER (flex-shrink-0 → never scrolls) ────────────── */}
        <header
          className={`flex-shrink-0 flex items-center justify-between px-6 py-3 bg-white border-b border-gray-200`}
          style={{ zIndex: 10 }}
        >
          <div className="flex items-center gap-3">
            <button
              onClick={toggleHistory}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-all duration-200 active:scale-95"
              title={historyOpen ? "Hide chat history" : "Show chat history"}
            >
              {historyOpen ? (
                <PanelLeftClose size={20} />
              ) : (
                <PanelLeft size={20} />
              )}
            </button>
            <div className={`${brandBlue} p-2 rounded-lg text-white shadow-md`}>
              <MessageSquare size={18} />
            </div>
            <div>
              <h2 className="font-bold text-gray-800 text-sm md:text-base leading-tight">
                ERP Assistant
              </h2>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-[10px] uppercase tracking-widest font-bold text-gray-400">
                  Online
                </span>
              </div>
            </div>
          </div>

          <div
            className={`w-9 h-9 rounded-lg ${brandBlue} text-white flex items-center justify-center text-xs font-bold`}
            title={loggedInUserName || "User"}
          >
            {userInitials}
          </div>
        </header>

        {/* ── SCROLLABLE MESSAGE AREA (flex-1 + min-h-0 → only this scrolls) */}
        <div
          ref={scrollZoneRef}
          onScroll={handleScroll}
          className="chat-scroll-zone"
          style={{
            flex: "1 1 0",
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
            background: "#fff",
            maxHeight: "100%",
          }}
        >
          <div className="w-full px-4 md:px-6 py-6 space-y-6">
            {/* Empty / loading states */}
            {loading ? (
              <div className="flex flex-col items-center justify-center mt-20 gap-3">
                <ThreeDotLoader />
                <p className="text-gray-400 text-sm">Loading conversation…</p>
              </div>
            ) : messages.length === 0 && !selectedChatId ? (
              <div className="flex flex-col items-center justify-center mt-20 gap-3">
                {/* <div className={`${brandBlue} p-4 rounded-2xl text-white shadow-lg`}>
                  <MessageSquare size={32} />
                </div> */}
                <p className="text-gray-500 text-sm font-medium">
                  How can I help you today?
                </p>
              </div>
            ) : messages.length === 0 && selectedChatId ? (
              <p className="text-center text-gray-400 mt-10">
                No messages in this conversation
              </p>
            ) : null}

            {/* Message list */}
            {messages.map((msg) => {
              const visibleContent = msg.sender === "ai"
                ? getVisibleAIText(msg.content)
                : msg.content;
              const displayAttachments =
                msg.sender === "user" && msg.attachments && msg.attachments.length > 0
                  ? msg.attachments
                  : [];
              const attachmentPreviewBlock =
                displayAttachments && displayAttachments.length > 0 ? (
                  <div className={`flex flex-wrap gap-1.5 ${visibleContent ? "mt-2" : ""}`}>
                    {displayAttachments.map((filename, idx) => {
                      // filename already includes folder prefix (e.g. "projects/report.pdf")
                      const fileUrl = `${process.env.REACT_APP_BASE_URL}/public/uploads/${filename}`;
                      if (isImageAttachment(filename)) {
                        return (
                          <a
                            key={idx}
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`block overflow-hidden rounded-xl border transition-all ${
                              msg.sender === "user"
                                ? "border-white/20 bg-white/10 hover:bg-white/20"
                                : "border-gray-200 bg-gray-50 hover:bg-gray-100"
                            }`}
                            title={filename}
                          >
                            <img
                              src={fileUrl}
                              alt={filename}
                              className="max-h-40 max-w-56 object-cover"
                            />
                          </a>
                        );
                      }

                      return (
                        <a
                          key={idx}
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                            msg.sender === "user"
                              ? "bg-white/20 text-white hover:bg-white/30"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          <FileText size={12} />
                          <span className="max-w-[120px] truncate">{filename}</span>
                        </a>
                      );
                    })}
                  </div>
                ) : null;
              const isHiddenPipelineLoader =
                msg.sender === "ai" &&
                msg.isLoading &&
                !msg.isHardConfirm &&
                !msg.isInteractive;
              const isEmptyAiPlaceholder =
                msg.sender === "ai" &&
                !msg.isLoading &&
                !msg.isHardConfirm &&
                !msg.isInteractive &&
                !visibleContent?.trim() &&
                !displayAttachments?.length &&
                !msg.steps?.length &&
                !msg.actionLogs?.length;

              if (isHiddenPipelineLoader || isEmptyAiPlaceholder) return null;

              return (
              <div
                key={msg._id}
                className={`flex gap-3 items-start max-w-[90%] md:max-w-[80%] group ${
                  msg.sender === "user" ? "flex-row-reverse ml-auto" : ""
                }`}
                onMouseEnter={() => setHoveredMessageId(msg._id)}
                onMouseLeave={() => setHoveredMessageId(null)}
              >
                {/* Avatar */}
                <div
                  className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-white shadow-sm font-bold text-[10px] ${
                    msg.sender === "user"
                      ? brandBlue
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {msg.sender === "user" ? userInitials : <MessageSquare size={15} />}
                </div>

                {/* Bubble */}
                <div
                  className={`p-3.5 rounded-2xl shadow-sm relative ${
                    msg.sender === "user"
                      ? `${brandBlue} text-white rounded-tr-none`
                      : "bg-white border border-gray-100 rounded-tl-none ring-1 ring-black/5"
                  }`}
                >
                  {msg.isLoading ? (
                    msg.steps && msg.steps.length > 0 ? (
                      <StepsTrace
                        steps={msg.steps}
                        active={msg._id === streamingIdRef.current && isSending}
                      />
                    ) : (
                      <ThreeDotLoader />
                    )
                  ) : msg.isHardConfirm && msg.hardConfirmData ? (
                    <ConfirmationCard
                      pendingActionId={msg.hardConfirmData.pendingActionId}
                      operations={msg.hardConfirmData.operations}
                      conversationId={msg.hardConfirmData.conversationId || selectedChatId || undefined}
                      aiSettingId={selectedModelId || undefined}
                      onConfirm={handleHardConfirm}
                      onCancel={handleHardCancel}
                    />
                  ) : msg.isInteractive ? (
                    <div className="space-y-5 min-w-[300px] max-w-[400px]">
                       <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                         <div className="flex items-center gap-2.5">
                           <div className="bg-[#0C447C]/10 p-1.5 rounded-lg text-[#0C447C]">
                             <MessageSquare size={16} />
                           </div>
                           <p className="text-sm font-bold text-gray-800 tracking-tight">
                             New {msg.interactiveData.collectionName} Action
                           </p>
                         </div>
                         <span className="text-[10px] font-bold px-2 py-0.5 bg-amber-50 text-amber-600 rounded-full border border-amber-100 uppercase tracking-wider">
                           Pending Info
                         </span>
                       </div>

                       <p className="text-xs text-gray-500 leading-relaxed bg-gray-50 p-2.5 rounded-xl border border-dashed border-gray-200">
                         Please provide the missing details to <strong>{msg.interactiveData.op}</strong> this record.
                       </p>

                       <div className="grid gap-4 py-1">
                         {msg.interactiveData.allFields.map((f: any) => (
                           <div key={f.name} className="group/field">
                             <div className="flex justify-between items-center mb-1.5">
                               <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest group-focus-within/field:text-[#0C447C] transition-colors">
                                 {f.label}
                               </label>
                               {f.required && (
                                 <span className="text-[9px] font-bold text-red-400 bg-red-50 px-1.5 py-0.5 rounded-md border border-red-100 uppercase">Required</span>
                               )}
                             </div>
                             <div className="relative">
                               <input
                                 type={f.type === 'number' ? 'number' : 'text'}
                                 placeholder={
                                   String(f.type || '').includes('ObjectId') || String(msg.interactiveData.allFields?.find((af: any) => af.name === f.name)?.type || '').includes('ObjectId')
                                     ? `Type name (e.g. "New", "Website", "Referral")`
                                     : `Enter ${f.label.toLowerCase()}…`
                                 }
                                 defaultValue={msg.interactiveData.partialData?.[f.name] || ''}
                                 required={f.required}
                                 className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:ring-4 focus:ring-[#0C447C]/5 focus:border-[#0C447C] focus:bg-white outline-none transition-all shadow-sm placeholder:text-gray-300"
                                 onChange={(e) => {
                                   msg.formData = {
                                     ...(msg.formData || msg.interactiveData.partialData || {}),
                                     [f.name]: e.target.value,
                                   };
                                 }}
                               />
                             </div>
                           </div>
                         ))}
                       </div>
                       
                       <div className="pt-2">
                         {msg.formError && (
                           <p className="text-[11px] text-center text-red-500 mb-2 font-semibold">
                             {msg.formError}
                           </p>
                         )}
                         <button
                           onClick={() => handleInterruptSubmit(msg.formData, msg._id, msg.interactiveData)}
                           className="w-full py-3 bg-[#0C447C] text-white rounded-xl text-sm font-bold shadow-lg shadow-[#0C447C]/20 hover:shadow-xl hover:shadow-[#0C447C]/30 hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-2 group"
                         >
                           <span>Submit Information</span>
                           <Send size={14} className="group-hover:translate-x-0.5 transition-transform" />
                         </button>
                         <p className="text-[9px] text-center text-gray-400 mt-3 uppercase font-semibold tracking-tighter">
                           Press submit once you have filled all required fields
                         </p>
                       </div>
                    </div>
                  ) : (
                    <>
                      {/* Claude-style step trace for AI messages */}
                      {msg.sender === "ai" && msg.steps && msg.steps.length > 0 && (
                        <StepsTrace steps={msg.steps} active={false} />
                      )}
                      {visibleContent && (
                        <div
                          className="max-w-full overflow-x-auto"
                          style={{ overflowY: "hidden" }}
                        >
                          <div
                            className="text-sm leading-relaxed"
                            dangerouslySetInnerHTML={{
                              __html:
                                msg.sender === "ai"
                                  ? formatAIResponse(visibleContent)
                                  : visibleContent,
                            }}
                          />
                        </div>
                      )}
                      {msg.sender === "user" && attachmentPreviewBlock}
                    </>
                  )}

                  {/* Undo buttons for write actions */}
                  {msg.sender === "ai" && !msg.isLoading && !msg.isInteractive && !msg.isHardConfirm && msg.actionLogs && msg.actionLogs.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {msg.actionLogs.map((al) =>
                        !undoneActionIds.has(al.actionLogId) ? (
                          <UndoButton
                            key={al.actionLogId}
                            actionLogId={al.actionLogId}
                            collectionName={al.collectionName}
                            operation={al.operation}
                            undoWindowSeconds={al.undoWindowSeconds}
                            onUndoed={handleUndoFromBubble}
                          />
                        ) : null
                      )}
                    </div>
                  )}

                  {/* Timestamp + copy */}
                  {!msg.isLoading && !msg.isInteractive && !msg.isHardConfirm && (
                    <div className="mt-2 flex justify-between items-center text-gray-400 pt-1.5 text-[10px]">
                      <span>
                        {new Date(msg.createdAt!).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                      <button
                        onClick={() =>
                          copyToClipboard(
                            msg.sender === "ai"
                              ? visibleContent.replace(/<[^>]*>/g, "")
                              : visibleContent,
                            msg._id,
                          )
                        }
                        className={`p-1 rounded transition-all duration-200 ${
                          hoveredMessageId === msg._id
                            ? msg.sender === "user"
                              ? "text-white/80 hover:text-white"
                              : "text-gray-500 hover:text-[#0C447C]"
                            : "opacity-0 pointer-events-none"
                        } ${copiedMessageId === msg._id ? "text-green-500" : ""}`}
                        title={copiedMessageId === msg._id ? "Copied!" : "Copy"}
                      >
                        {copiedMessageId === msg._id ? (
                          <Check size={13} />
                        ) : (
                          <Copy size={13} />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              );
            })}

            {/* Scroll anchor */}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* ── THINKING INDICATOR (outside chat box, above input) ──────── */}
        {showThinkingIndicator && (
          <div
            style={{
              padding: "10px 24px",
              background: "#f8fafc",
              borderTop: "1px solid #f1f5f9",
              animation: "fadeSlideIn 0.3s ease",
            }}
          >
            {liveSteps.length > 0 ? (
              <StepsTrace steps={liveSteps} active={true} />
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#0C447C", animation: "pulse 1.4s infinite ease-in-out" }} />
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#0C447C", animation: "pulse 1.4s infinite ease-in-out", animationDelay: "0.2s" }} />
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#0C447C", animation: "pulse 1.4s infinite ease-in-out", animationDelay: "0.4s" }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 500, color: "#475569", letterSpacing: "0.01em" }}>
                  {thinkingStatus || "Thinking..."}
                </span>
              </div>
            )}
          </div>
        )}

        {/* INPUT BAR — always sticky at bottom, never hides */}
        <div style={{ zIndex: 10, position: "relative" }}>
          <button
            type="button"
            onClick={scrollToBottom}
            aria-label="Scroll to latest message"
            title="Scroll to latest message"
            className={`absolute left-1/2 -top-12 z-20 flex h-10 w-10 -translate-x-1/2 items-center justify-center rounded-full bg-[#0C447C] text-white shadow-lg shadow-[#0C447C]/25 transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-xl active:scale-95 ${
              showScrollToBottom
                ? "opacity-100 translate-y-0 scale-100 pointer-events-auto"
                : "opacity-0 translate-y-4 scale-95 pointer-events-none"
            }`}
          >
            <ArrowDown size={18} strokeWidth={2.5} />
          </button>

          {/* Quick suggestions — new chat sessions only */}
          {!selectedChatId && messages.length === 0 && (
            <div className="flex flex-wrap justify-center gap-2 pt-3 px-4">
              {QuickQueries.map((query) => (
                <button
                  key={query}
                  onClick={() => handleQuickQuery(query)}
                  disabled={isSending}
                  className="px-3 py-1.5 bg-white border border-gray-200 rounded-full text-xs font-medium text-gray-500 hover:border-[#0C447C] hover:text-[#0C447C] transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {query}
                </button>
              ))}
            </div>
          )}

          {/* Soft confirm toasts */}
          {softConfirms.map((sc) => (
            <SoftConfirmToast
              key={sc.id}
              actionLogIds={sc.actionLogIds}
              operations={sc.operations}
              undoWindowSeconds={sc.undoWindowSeconds}
              onUndo={handleSoftUndo}
              onDismiss={() => handleSoftDismiss(sc.id)}
            />
          ))}

          {/* Input box */}
          <div className="px-4 pt-3 md:px-8 md:pb-6">
            <div className="w-full">
              <div
                className="relative bg-white rounded-2xl shadow-md focus-within:shadow-lg focus-within:shadow-[rgba(12,68,124,0.15)] transition-all duration-200 border border-gray-100"
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragOver(false);
                  if (e.dataTransfer.files.length > 0) {
                    handleFileDrop(e.dataTransfer.files);
                  }
                }}
              >
                {/* ── Textarea row ── */}
                <div className="flex items-end gap-3 px-4 pt-3 pb-2">
                  <textarea
                    ref={textareaRef}
                    rows={1}
                    placeholder="Message ERP Assistant…"
                    value={inputValue}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="flex-1 resize-none border-none outline-none focus:ring-0 focus:outline-none focus:border-none text-sm text-gray-800 bg-transparent placeholder:text-gray-400 leading-relaxed max-h-[180px] overflow-y-auto py-0.5"
                    style={{ scrollbarWidth: "none" }}
                  />
                </div>

                {/* ── File drop zone overlay + chips ── */}
                <FileDropZone
                  ref={fileDropZoneRef}
                  isDragOver={isDragOver}
                  onDrop={handleFileDrop}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  attachedFiles={attachedFiles}
                  onRemoveFile={handleRemoveFile}
                  uploading={fileUploading}
                />

                {/* ── Bottom toolbar row (paperclip + model chip + send button) ── */}
                <div className="flex items-center justify-between gap-2 px-3 pb-2.5">
                  {/* Paperclip button */}
                  <button
                    type="button"
                    onClick={() => fileDropZoneRef.current?.openFilePicker()}
                    disabled={fileUploading}
                    className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-xl text-gray-400 hover:text-[#0C447C] hover:bg-gray-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Attach file"
                    aria-label="Attach a file"
                  >
                    <Paperclip size={16} />
                  </button>

                  <div className="flex items-center gap-2">
                    {/* Model selector — Claude-style chip */}
                    <ModelSelector
                      options={modelOptions}
                      selected={selectedModelId}
                      onChange={setSelectedModelId}
                      disabled={isSending}
                    />

                    {/* Send button */}
                    <button
                      onClick={handleSend}
                      disabled={isSending || fileUploading || (!inputValue.trim() && attachedFiles.length === 0)}
                      className={`flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-xl transition-all duration-200 active:scale-95 ${
                        (inputValue.trim() || attachedFiles.length > 0) && !isSending && !fileUploading
                          ? `${brandBlue} text-white shadow-md hover:opacity-90`
                          : "bg-gray-100 text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      <Send
                        size={15}
                        fill={(inputValue.trim() || attachedFiles.length > 0) && !isSending && !fileUploading ? "currentColor" : "none"}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <p className="text-center text-[10px] text-gray-400 mt-2">
                <span className="font-semibold">Enter</span> to send ·{" "}
                <span className="font-semibold">Shift + Enter</span> for new line
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;
